import React, { useRef } from 'react';

export interface TabDef {
  key: string;
  label: string;
  disabled?: boolean;
}

export interface TabsProps {
  tabs: TabDef[];
  activeKey: string;
  onChange: (key: string) => void;
  children?: React.ReactNode;
}

const Tabs: React.FC<TabsProps> = ({ tabs, activeKey, onChange, children }) => {
  const buttonsRef = useRef<Array<HTMLButtonElement | null>>([]);

  const focusableIndexes = tabs
    .map((t, i) => (t.disabled ? -1 : i))
    .filter((i) => i >= 0);

  const moveFocus = (currentIdx: number, dir: 1 | -1 | 'first' | 'last') => {
    if (focusableIndexes.length === 0) return;
    let nextIdx: number;
    if (dir === 'first') {
      nextIdx = focusableIndexes[0];
    } else if (dir === 'last') {
      nextIdx = focusableIndexes[focusableIndexes.length - 1];
    } else {
      const pos = focusableIndexes.indexOf(currentIdx);
      const nextPos =
        (pos + dir + focusableIndexes.length) % focusableIndexes.length;
      nextIdx = focusableIndexes[nextPos];
    }
    onChange(tabs[nextIdx].key);
    buttonsRef.current[nextIdx]?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent, idx: number) => {
    switch (e.key) {
      case 'ArrowRight':
        e.preventDefault();
        moveFocus(idx, 1);
        break;
      case 'ArrowLeft':
        e.preventDefault();
        moveFocus(idx, -1);
        break;
      case 'Home':
        e.preventDefault();
        moveFocus(idx, 'first');
        break;
      case 'End':
        e.preventDefault();
        moveFocus(idx, 'last');
        break;
    }
  };

  return (
    <div>
      <div role="tablist" className="flex border-b border-gray-200">
        {tabs.map((tab, idx) => {
          const active = tab.key === activeKey;
          return (
            <button
              key={tab.key}
              ref={(el) => {
                buttonsRef.current[idx] = el;
              }}
              role="tab"
              aria-selected={active}
              aria-disabled={tab.disabled}
              tabIndex={active ? 0 : -1}
              disabled={tab.disabled}
              onClick={() => !tab.disabled && onChange(tab.key)}
              onKeyDown={(e) => handleKeyDown(e, idx)}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 ${
                active
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
              } ${tab.disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
            >
              {tab.label}
            </button>
          );
        })}
      </div>
      <div className="pt-4">{children}</div>
    </div>
  );
};

export default Tabs;
